/**
 * Shared helpers and presentational bits for the invoicing-incidents screens
 * (list + dedicated detail page) and the bill-detail Hacienda card.
 */

import {
  AlertTriangle,
  Building2,
  CheckCircle2,
  Clock,
  Copy,
  FileX,
  HelpCircle,
  KeyRound,
  Percent,
  RefreshCw,
  ScrollText,
  Shield,
  UserX,
  WifiOff,
  XCircle,
} from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { HACIENDA_STATUS_COLORS, HACIENDA_STATUS_LABELS } from '@/types'
import { cn } from '@/lib/utils'
import { t } from '@/i18n'
import type { IncidentWorkflowStatus, ParsedError } from '@/hooks/useInvoicing'

export const WORKFLOW_CONFIG: Record<
  IncidentWorkflowStatus,
  { label: string; color: string }
> = {
  open: {
    label: t('incidents.statusOpen'),
    color: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  },
  in_progress: {
    label: t('incidents.statusInProgress'),
    color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  },
  resolved: {
    label: t('incidents.statusResolved'),
    color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  },
  dismissed: {
    label: t('incidents.statusDismissed'),
    color: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  },
}

export const TYPE_LABELS: Record<string, string> = {
  submission_error: t('incidents.typeSubmissionError'),
  rejected: t('incidents.typeRejected'),
  connection_error: t('incidents.typeConnectionError'),
}

// --------------------------------------------------------------------------- //
// Error category presentation (keyed by classifier slug — see incident_classifier.py)
// --------------------------------------------------------------------------- //

type CategoryIcon = typeof AlertTriangle

export interface CategoryPresentation {
  label: string
  description: string
  action: string
  icon: CategoryIcon
  chipClass: string
  blockClass: string
}

export const CATEGORY_PRESENTATION: Record<string, CategoryPresentation> = {
  cabys_invalid: {
    label: t('incidents.categories.cabys_invalid.label'),
    description: t('incidents.categories.cabys_invalid.description'),
    action: t('incidents.categories.cabys_invalid.action'),
    icon: FileX,
    chipClass: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
    blockClass: 'bg-orange-50 dark:bg-orange-950/40 text-orange-800 dark:text-orange-200',
  },
  receiver_id_format: {
    label: t('incidents.categories.receiver_id_format.label'),
    description: t('incidents.categories.receiver_id_format.description'),
    action: t('incidents.categories.receiver_id_format.action'),
    icon: UserX,
    chipClass: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
    blockClass: 'bg-amber-50 dark:bg-amber-950/40 text-amber-800 dark:text-amber-200',
  },
  receiver_id_not_found: {
    label: t('incidents.categories.receiver_id_not_found.label'),
    description: t('incidents.categories.receiver_id_not_found.description'),
    action: t('incidents.categories.receiver_id_not_found.action'),
    icon: UserX,
    chipClass: 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300',
    blockClass: 'bg-rose-50 dark:bg-rose-950/40 text-rose-800 dark:text-rose-200',
  },
  receiver_activity_invalid: {
    label: t('incidents.categories.receiver_activity_invalid.label'),
    description: t('incidents.categories.receiver_activity_invalid.description'),
    action: t('incidents.categories.receiver_activity_invalid.action'),
    icon: Building2,
    chipClass: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
    blockClass: 'bg-yellow-50 dark:bg-yellow-950/40 text-yellow-800 dark:text-yellow-200',
  },
  sender_activity_invalid: {
    label: t('incidents.categories.sender_activity_invalid.label'),
    description: t('incidents.categories.sender_activity_invalid.description'),
    action: t('incidents.categories.sender_activity_invalid.action'),
    icon: Building2,
    chipClass: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
    blockClass: 'bg-yellow-50 dark:bg-yellow-950/40 text-yellow-800 dark:text-yellow-200',
  },
  tax_inconsistent: {
    label: t('incidents.categories.tax_inconsistent.label'),
    description: t('incidents.categories.tax_inconsistent.description'),
    action: t('incidents.categories.tax_inconsistent.action'),
    icon: Percent,
    chipClass: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
    blockClass: 'bg-purple-50 dark:bg-purple-950/40 text-purple-800 dark:text-purple-200',
  },
  duplicate_consecutive: {
    label: t('incidents.categories.duplicate_consecutive.label'),
    description: t('incidents.categories.duplicate_consecutive.description'),
    action: t('incidents.categories.duplicate_consecutive.action'),
    icon: ScrollText,
    chipClass: 'bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300',
    blockClass: 'bg-sky-50 dark:bg-sky-950/40 text-sky-800 dark:text-sky-200',
  },
  certificate: {
    label: t('incidents.categories.certificate.label'),
    description: t('incidents.categories.certificate.description'),
    action: t('incidents.categories.certificate.action'),
    icon: Shield,
    chipClass: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
    blockClass: 'bg-red-50 dark:bg-red-950/40 text-red-800 dark:text-red-200',
  },
  auth: {
    label: t('incidents.categories.auth.label'),
    description: t('incidents.categories.auth.description'),
    action: t('incidents.categories.auth.action'),
    icon: KeyRound,
    chipClass: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
    blockClass: 'bg-red-50 dark:bg-red-950/40 text-red-800 dark:text-red-200',
  },
  connection: {
    label: t('incidents.categories.connection.label'),
    description: t('incidents.categories.connection.description'),
    action: t('incidents.categories.connection.action'),
    icon: WifiOff,
    chipClass: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
    blockClass: 'bg-blue-50 dark:bg-blue-950/40 text-blue-800 dark:text-blue-200',
  },
  other: {
    label: t('incidents.categories.other.label'),
    description: t('incidents.categories.other.description'),
    action: t('incidents.categories.other.action'),
    icon: HelpCircle,
    chipClass: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200',
    blockClass: 'bg-gray-50 dark:bg-gray-950/40 text-gray-800 dark:text-gray-200',
  },
}

// Stable order for filter UIs. Keep this aligned with `ALL_CATEGORIES` in
// the backend `incident_classifier.py` — adding/removing here without
// updating the backend (or vice versa) silently drops categories from the
// filter dropdown or the histogram.
export const CATEGORY_ORDER = [
  'cabys_invalid',
  'receiver_id_format',
  'receiver_id_not_found',
  'receiver_activity_invalid',
  'sender_activity_invalid',
  'tax_inconsistent',
  'duplicate_consecutive',
  'certificate',
  'auth',
  'connection',
  'other',
] as const

export function categoryFor(slug: string): CategoryPresentation {
  return CATEGORY_PRESENTATION[slug] || CATEGORY_PRESENTATION.other
}


export const KIND_LABELS: Record<string, string> = {
  bill: 'Factura / Tiquete',
  purchase: 'Compra',
  rep: 'Recibo de pago',
}

export function formatDate(dateString: string | null) {
  if (!dateString) return '-'
  return new Date(dateString).toLocaleString('es-CR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function formatCurrency(amount: number) {
  return new Intl.NumberFormat('es-CR', {
    style: 'currency',
    currency: 'CRC',
    maximumFractionDigits: 0,
  }).format(amount)
}

export function formatJson(str: string): string {
  try {
    return JSON.stringify(JSON.parse(str), null, 2)
  } catch {
    return str
  }
}

export function TypeIcon({ type }: { type: string }) {
  if (type === 'rejected') return <XCircle className="h-3 w-3" />
  if (type === 'connection_error') return <RefreshCw className="h-3 w-3" />
  return <AlertTriangle className="h-3 w-3" />
}

// Hacienda statuses not covered by the shared billing maps.
const HACIENDA_EXTRA_LABELS: Record<string, string> = {
  received: 'Recibido (sin veredicto)',
}
const HACIENDA_EXTRA_COLORS: Record<string, string> = {
  received: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
}

function HaciendaStatusIcon({ status }: { status: string }) {
  switch (status) {
    case 'accepted':
    case 'success':
      return <CheckCircle2 className="h-3 w-3" />
    case 'rejected':
    case 'failed':
    case 'error':
    case 'config_error':
      return <XCircle className="h-3 w-3" />
    case 'retrying':
      return <RefreshCw className="h-3 w-3 animate-spin" />
    case 'received':
      return <RefreshCw className="h-3 w-3" />
    default:
      return <Clock className="h-3 w-3" />
  }
}

/**
 * Colored badge for a document's live Hacienda status, reusing the same
 * label/color scheme as the billing screens for consistency.
 */
export function HaciendaStatusBadge({ status }: { status?: string | null }) {
  if (!status) {
    return (
      <Badge variant="outline" className="text-xs gap-1">
        <Clock className="h-3 w-3" />
        {t('incidents.notSent')}
      </Badge>
    )
  }
  const label = HACIENDA_STATUS_LABELS[status] || HACIENDA_EXTRA_LABELS[status] || status
  const color =
    HACIENDA_STATUS_COLORS[status] ||
    HACIENDA_EXTRA_COLORS[status] ||
    'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
  return (
    <Badge className={cn('text-xs gap-1', color)}>
      <HaciendaStatusIcon status={status} />
      {label}
    </Badge>
  )
}

/**
 * Tone (color family + title) for a Hacienda message box, derived from the
 * live document status — so a success/info message is never shown as an error.
 */
export function haciendaMessageTone(
  liveStatus: string | null | undefined,
  incidentType: string
): 'ok' | 'error' | 'info' {
  if (liveStatus) {
    if (liveStatus === 'accepted' || liveStatus === 'success') return 'ok'
    if (['rejected', 'failed', 'error', 'config_error'].includes(liveStatus)) return 'error'
    return 'info' // pending / submitted / received / retrying
  }
  // No live status yet: fall back to the incident classification.
  return incidentType === 'connection_error' ? 'info' : 'error'
}

export function JsonBlock({
  label,
  json,
  onCopy,
}: {
  label: string
  json: string
  onCopy: () => void
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <p className="text-xs font-medium">{label}</p>
        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={onCopy}>
          <Copy className="h-3 w-3" />
        </Button>
      </div>
      <pre className="text-xs bg-muted p-2 rounded overflow-x-auto max-h-48">{json}</pre>
    </div>
  )
}

/**
 * Small chip showing one error category — used to summarize a multi-error
 * incident in the list view.
 */
export function CategoryChip({ category }: { category: string }) {
  const p = categoryFor(category)
  const Icon = p.icon
  return (
    <Badge variant="outline" className={cn('gap-1 text-xs border-transparent', p.chipClass)}>
      <Icon className="h-3 w-3" />
      {p.label}
    </Badge>
  )
}

/**
 * Friendly rendering of a (possibly multi-error) Hacienda/Alanube message.
 *
 * Used by the incident list cards (compact), the incident detail page and the
 * bill-detail Hacienda card. Each parsed error gets a title + suggested action;
 * the raw text and the full original message are accessible but not in the way.
 */
export function FriendlyErrors({
  errors,
  rawMessage,
  variant = 'detail',
}: {
  errors: ParsedError[]
  rawMessage?: string | null
  variant?: 'detail' | 'compact'
}) {
  // Compact does not surface rawMessage at all, so it needs at least one error
  // to render anything meaningful; detail also handles a raw-only payload.
  if (variant === 'compact' ? !errors.length : !errors.length && !rawMessage) return null

  if (variant === 'compact') {
    const first = errors[0]
    const p = categoryFor(first.category)
    return (
      <div className="space-y-1">
        <div className="flex flex-wrap gap-1">
          {errors.slice(0, 3).map((e, i) => (
            <CategoryChip key={i} category={e.category} />
          ))}
          {errors.length > 3 && (
            <Badge variant="outline" className="text-xs">
              +{errors.length - 3}
            </Badge>
          )}
        </div>
        <p className="text-xs text-red-600 dark:text-red-400 line-clamp-2 break-words">
          {p.label}
          {first.target_label
            ? ` · ${first.target_label}`
            : first.target
              ? ` · ${first.target}`
              : ''}
          {errors.length > 1 ? ` ${t('incidents.moreErrors', { count: errors.length - 1 })}` : ''}
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      {errors.map((e, i) => {
        const p = categoryFor(e.category)
        const Icon = p.icon
        return (
          <div key={i} className={cn('p-3 rounded-lg', p.blockClass)}>
            <div className="flex items-start gap-2">
              <Icon className="h-4 w-4 mt-0.5 shrink-0" aria-hidden="true" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold flex flex-wrap items-baseline gap-x-2">
                  <span>{p.label}</span>
                  {e.code ? (
                    <span className="font-mono text-xs opacity-70">[{e.code}]</span>
                  ) : null}
                  {e.target && !e.target_label ? (
                    <span className="font-mono text-xs opacity-70 break-all">
                      {e.target}
                    </span>
                  ) : null}
                </p>
                {e.target_label ? (
                  <p className="text-xs mt-0.5 font-medium break-words">
                    {e.target_label}
                  </p>
                ) : null}
                <p className="text-xs mt-0.5 opacity-90">{p.description}</p>
                <p className="text-xs mt-1">
                  <span className="font-medium">{t('incidents.whatToDo')} </span>
                  {p.action}
                </p>
                <details className="mt-1">
                  <summary className="text-xs cursor-pointer opacity-70 hover:opacity-100">
                    {t('incidents.viewRawMessage')}
                  </summary>
                  <pre className="text-xs mt-1 font-mono opacity-90 whitespace-pre-wrap break-all overflow-x-auto">
                    {e.raw}
                  </pre>
                </details>
              </div>
            </div>
          </div>
        )
      })}
      {rawMessage && errors.length > 1 && (
        <details>
          <summary className="text-xs cursor-pointer text-muted-foreground hover:text-foreground">
            {t('incidents.viewFullMessage')}
          </summary>
          <pre className="text-xs mt-1 p-2 rounded bg-muted font-mono whitespace-pre-wrap break-all overflow-x-auto">
            {rawMessage}
          </pre>
        </details>
      )}
    </div>
  )
}
